import { useState, useRef, useEffect, useCallback } from 'react'
import { snapshot } from 'valtio'
import { store, actions } from '@/store'
import { haptics } from '@/lib/haptics'
import { toast } from 'sonner'
import { Trash2, Copy, CheckCircle2, Circle } from 'lucide-react'
import { useSounds } from '@/hooks/use-sounds'
import { showUndoToast } from '@/lib/toast-utils'

export function useTodoSelection() {
  const [selectedIds, setSelectedIds] = useState(new Set())
  const selectedIdsRef = useRef(selectedIds)
  const [isSelecting, setIsSelecting] = useState(false)
  const { playDoneSound, playDeleteSound } = useSounds()
  const [recentlyDuplicatedIds, setRecentlyDuplicatedIds] = useState(new Set())
  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  // Dismiss toasts when selecting
  useEffect(() => {
    if (selectedIds.size > 0) {
      toast.dismiss()
    }
  }, [selectedIds])
  const generateNextName = useCallback((baseText, todos = store.todos) => {
    const match = baseText.match(/^(.*?) - (\d+)$/)
    let rootName = baseText
    if (match) {
      rootName = match[1]
    }
    const suffixes = []
    todos.forEach((t) => {
      if (t.text === rootName) {
        suffixes.push(0)
      } else {
        const m = t.text.match(/^(.*?) - (\d+)$/)
        if (m && m[1] === rootName) {
          suffixes.push(Number.parseInt(m[2], 10))
        }
      }
    })
    const maxSuffix = suffixes.length > 0 ? Math.max(...suffixes) : 0
    return `${rootName} - ${maxSuffix + 1}`
  }, [])
  const bulkDelete = useCallback(() => {
    const selected = selectedIdsRef.current
    selected.forEach((id) => actions.deleteTodo(id))
    setSelectedIds(new Set())
    haptics.success()
    playDeleteSound()
    showUndoToast(`${selected.size} tasks deleted`, <Trash2 className="h-4 w-4 text-destructive" />)
  }, [playDeleteSound])
  const updateRecentlyDuplicatedIds = useCallback((ids, shouldAdd) => {
    setRecentlyDuplicatedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => {
        if (shouldAdd) {
          next.add(id)
        } else {
          next.delete(id)
        }
      })
      return next
    })
  }, [])
  const bulkDuplicate = useCallback(() => {
    const selected = selectedIdsRef.current
    if (selected.size === 0) return

    // Performance optimization: Snapshot store once to avoid repeated proxy traps in loops
    const todosSnapshot = snapshot(store.todos)
    const createdNames = new Set()
    const duplicates = []
    let lastSelectedIndex = -1

    todosSnapshot.forEach((todo, index) => {
      if (selected.has(todo.id)) {
        lastSelectedIndex = index
        let candidateName = generateNextName(todo.text, todosSnapshot)
        let safety = 0

        // Ensure name is unique within the current batch being created too
        while (createdNames.has(candidateName) && safety < 100) {
          const match = candidateName.match(/^(.*?) - (\d+)$/)
          if (match) {
            const root = match[1]
            const suffix = Number.parseInt(match[2], 10)
            candidateName = `${root} - ${suffix + 1}`
          } else {
            candidateName = `${candidateName} - 1`
          }
          safety++
        }
        createdNames.add(candidateName)
        duplicates.push({
          ...todo,
          id: crypto.randomUUID(),
          text: candidateName,
          timeSpent: 0,
          subtasks: (todo.subtasks || []).map((s) => ({
            ...s,
          })),
          comments: (todo.comments || []).map((c) => ({
            ...c,
            id: crypto.randomUUID(),
          })),
          lastStartTime: undefined,
        })
      }
    })
    if (duplicates.length > 0 && lastSelectedIndex !== -1) {
      // Need to insert at specific index? Value store.todos is a proxy array.
      store.todos.splice(lastSelectedIndex + 1, 0, ...duplicates)
      setSelectedIds(new Set())
      const newIds = duplicates.map((t) => t.id)
      updateRecentlyDuplicatedIds(newIds, true)
      setTimeout(() => {
        updateRecentlyDuplicatedIds(newIds, false)
      }, 2200)
      haptics.success()
      showUndoToast(`${duplicates.length} tasks duplicated`, <Copy className="h-4 w-4" />)
    }
  }, [generateNextName, updateRecentlyDuplicatedIds])
  const bulkComplete = useCallback(
    (complete) => {
      const selected = selectedIdsRef.current
      selected.forEach((id) =>
        actions.updateTodo(id, {
          completed: complete,
          status: complete ? 'done' : 'todo',
        }),
      )
      setSelectedIds(new Set())
      haptics.success()
      if (complete) playDoneSound()
      showUndoToast(
        complete ? 'Selected tasks completed' : 'Selected tasks active',
        complete ? (
          <CheckCircle2 className="h-4 w-4 text-primary" />
        ) : (
          <Circle className="h-4 w-4" />
        ),
      )
    },
    [playDoneSound],
  )
  const handleItemSelect = useCallback((id, multi) => {
    setSelectedIds((prev) => {
      if (multi) {
        const newSelected = new Set(prev)
        if (newSelected.has(id)) {
          newSelected.delete(id)
        } else {
          newSelected.add(id)
        }
        return newSelected
      } else {
        return new Set([id])
      }
    })
  }, [])
  return {
    selectedIds,
    setSelectedIds,
    selectedIdsRef,
    isSelecting,
    setIsSelecting,
    recentlyDuplicatedIds,
    setRecentlyDuplicatedIds,
    bulkDelete,
    bulkDuplicate,
    bulkComplete,
    handleItemSelect,
    generateNextName,
  }
}
